// Müşteri yöneticisi API sarmalayıcıları (M16 T20, K13).
//   Portal (müşteri yöneticisi): /api/v1/client-admin/* — whitelist prefix'i, ikinci kat kapı
//   `adminClientIds` (403 CLIENT_ADMIN_FORBIDDEN). Danışman: /api/v1/clients/:id/admins.
// Şema/tipler packages/shared/src/schemas/client-admin.ts ile birebir.

import type {
  ClientAdminFirm,
  ClientAdminProject,
  ClientAdminRow,
  ClientAdminSetsResponse,
  ClientKpiUsersResponse,
  ClientUser,
  ClientUsersResponse,
  GrantClientAdminRequest,
  GrantClientAdminResponse,
  InviteClientUserRequest,
  InviteClientUserResponse,
  InviteParticipantRequest,
  InviteParticipantResponse,
  KpiAccessGrant,
  UpdateClientAdminRequest,
} from "@mahir/shared";
import { api } from "./client";

const base = (clientId: string): string => `/client-admin/${clientId}`;

// ─── Portal ─────────────────────────────────────────────────────────────────

export async function listAdminFirms(): Promise<ClientAdminFirm[]> {
  return (await api<{ firms: ClientAdminFirm[] }>("/client-admin/firms")).firms;
}

export async function listClientUsers(clientId: string): Promise<ClientUsersResponse> {
  return api<ClientUsersResponse>(`${base(clientId)}/users`);
}

export async function inviteClientUser(
  clientId: string,
  body: InviteClientUserRequest,
): Promise<InviteClientUserResponse> {
  return api<InviteClientUserResponse>(`${base(clientId)}/users/invite`, {
    method: "POST",
    body,
  });
}

export async function resendClientInvite(
  clientId: string,
  userId: string,
): Promise<{ mailSent: boolean }> {
  return api<{ mailSent: boolean }>(`${base(clientId)}/users/${userId}/resend-invite`, {
    method: "POST",
    body: {},
  });
}

export async function deactivateClientUser(
  clientId: string,
  userId: string,
): Promise<{ globallyClosed: boolean }> {
  return api<{ globallyClosed: boolean }>(`${base(clientId)}/users/${userId}/deactivate`, {
    method: "POST",
    body: {},
  });
}

/** `expected`: yazılan/silinen her set için okunan erişim jetonu (null = satır yoktu) — 409 KPI_ACCESS_STALE. */
export async function setKpiAccess(
  clientId: string,
  userId: string,
  grants: KpiAccessGrant[],
  expected: Record<string, string | null>,
): Promise<{ user: ClientUser; materializedSetIds: string[] }> {
  const r = await api<{ user: ClientUser; materializedSetIds?: string[] }>(
    `${base(clientId)}/users/${userId}/kpi-access`,
    { method: "PUT", body: { grants, expected } },
  );
  // X1-m2 — "Tüm kategoriler" açık listeye döndüyse sunucu set kimliklerini söyler
  return { user: r.user, materializedSetIds: r.materializedSetIds ?? [] };
}

export async function listClientAdminSets(clientId: string): Promise<ClientAdminSetsResponse> {
  return api<ClientAdminSetsResponse>(`${base(clientId)}/sets`);
}

// ─── Portal: Yöneticiler (yalnız Süper Admin) ───────────────────────────────

export async function listPortalClientAdmins(clientId: string): Promise<ClientAdminRow[]> {
  return (await api<{ admins: ClientAdminRow[] }>(`${base(clientId)}/admins`)).admins;
}

export async function grantPortalClientAdmin(
  clientId: string,
  body: GrantClientAdminRequest,
): Promise<GrantClientAdminResponse> {
  return api<GrantClientAdminResponse>(`${base(clientId)}/admins`, { method: "POST", body });
}

export async function updatePortalClientAdmin(
  clientId: string,
  userId: string,
  body: UpdateClientAdminRequest,
): Promise<ClientAdminRow> {
  return (
    await api<{ admin: ClientAdminRow }>(`${base(clientId)}/admins/${userId}`, {
      method: "PATCH",
      body,
    })
  ).admin;
}

export async function revokePortalClientAdmin(clientId: string, userId: string): Promise<void> {
  await api<void>(`${base(clientId)}/admins/${userId}`, { method: "DELETE" });
}

export async function listClientAdminProjects(clientId: string): Promise<ClientAdminProject[]> {
  return (await api<{ projects: ClientAdminProject[] }>(`${base(clientId)}/projects`)).projects;
}

export async function inviteToProject(
  clientId: string,
  projectId: string,
  body: InviteParticipantRequest,
): Promise<InviteParticipantResponse> {
  return api<InviteParticipantResponse>(
    `${base(clientId)}/projects/${projectId}/participants/invite`,
    { method: "POST", body },
  );
}

// ─── Danışman ───────────────────────────────────────────────────────────────

export async function listClientAdmins(clientId: string): Promise<ClientAdminRow[]> {
  return (await api<{ admins: ClientAdminRow[] }>(`/clients/${clientId}/admins`)).admins;
}

export async function grantClientAdmin(
  clientId: string,
  body: GrantClientAdminRequest,
): Promise<GrantClientAdminResponse> {
  return api<GrantClientAdminResponse>(`/clients/${clientId}/admins`, { method: "POST", body });
}

export async function updateClientAdmin(
  clientId: string,
  userId: string,
  body: UpdateClientAdminRequest,
): Promise<ClientAdminRow> {
  return (
    await api<{ admin: ClientAdminRow }>(`/clients/${clientId}/admins/${userId}`, {
      method: "PATCH",
      body,
    })
  ).admin;
}

export async function revokeClientAdmin(clientId: string, userId: string): Promise<void> {
  await api<void>(`/clients/${clientId}/admins/${userId}`, { method: "DELETE" });
}

// ─── Danışman: KPI set erişimi (TODO-KPI-19) — /clients/:id/kpi-users/* ────

export async function listClientKpiUsers(clientId: string): Promise<ClientKpiUsersResponse> {
  return api<ClientKpiUsersResponse>(`/clients/${clientId}/kpi-users`);
}

export async function setKpiAccessAsConsultant(
  clientId: string,
  userId: string,
  grants: KpiAccessGrant[],
  expected: Record<string, string | null>,
): Promise<ClientUser> {
  return (
    await api<{ user: ClientUser }>(`/clients/${clientId}/kpi-users/${userId}/access`, {
      method: "PUT",
      body: { grants, expected },
    })
  ).user;
}

export async function inviteClientKpiUser(
  clientId: string,
  body: InviteClientUserRequest,
): Promise<InviteClientUserResponse> {
  return api<InviteClientUserResponse>(`/clients/${clientId}/kpi-users/invite`, {
    method: "POST",
    body,
  });
}
